import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ChevronRight, Brain, RefreshCw, 
  ArrowRight, Briefcase, X, Play, GripVertical, CheckCircle, Sparkles
} from 'lucide-react';
import { useAppContext } from '../../core/App';
import { useTelemetry } from '../../core/hooks/useTelemetry';
import { db } from '../../core/firebase'; 
import { doc, updateDoc } from 'firebase/firestore'; 
import { scenarioBank } from '../../data/ScenarioBank';

// --- NATIVE HTML5 DRAG & DROP COMPONENT ---
// Keeps DOM interactions isolated for the overarching SDK to naturally capture telemetry.
const DraggableList = ({ options, onConfirm }) => {
  const [items, setItems] = useState(options);
  const dragItem = useRef(null);
  const dragOverItem = useRef(null);

  const handleDragStart = (e, position) => {
    dragItem.current = position;
    // This empty dataTransfer is required for Firefox HTML5 drag/drop support
    e.dataTransfer.setData('text/plain', ''); 
    e.currentTarget.style.opacity = '0.5';
  };

  const handleDragEnter = (e, position) => {
    e.preventDefault();
    dragOverItem.current = position;
  };

  const handleDragEnd = (e) => {
    e.preventDefault();
    e.currentTarget.style.opacity = '1';
    
    if (dragItem.current !== null && dragOverItem.current !== null && dragItem.current !== dragOverItem.current) {
      const _items = [...items];
      const draggedItemContent = _items.splice(dragItem.current, 1)[0];
      _items.splice(dragOverItem.current, 0, draggedItemContent);
      setItems(_items);
    }
    dragItem.current = null;
    dragOverItem.current = null;
  };

  return (
    <div className="w-full space-y-4" data-telemetry-target="drag-drop-list">
      {items.map((item, index) => (
        <motion.div
          key={item}
          draggable
          onDragStart={(e) => handleDragStart(e, index)}
          onDragEnter={(e) => handleDragEnter(e, index)}
          onDragOver={(e) => e.preventDefault()}
          onDragEnd={handleDragEnd}
          layout
          className="flex items-center gap-4 p-5 bg-white border-2 border-slate-100 rounded-2xl shadow-sm hover:border-indigo-400 hover:shadow-md transition-all cursor-grab active:cursor-grabbing"
        >
          <GripVertical className="text-slate-400 flex-shrink-0" />
          <span className="text-sm font-bold text-slate-700 pointer-events-none select-none">
            <span className="mr-3 bg-slate-100 text-slate-500 px-2 py-1 rounded-full text-xs">{index + 1}</span>
            {item}
          </span>
        </motion.div>
      ))}
      
      <button 
        onClick={() => onConfirm(items)}
        className="w-full mt-6 py-4 bg-indigo-600 text-white font-black rounded-xl shadow-lg hover:bg-indigo-700 transition flex items-center justify-center gap-2"
      >
         <CheckCircle size={18} /> Confirm Ranking
      </button>
    </div>
  );
};

// --- SLIDER INTERACTION COMPONENT ---
const SliderInteraction = ({ scenario, onConfirm }) => {
  const [val, setVal] = useState(50);
  return (
    <div className="w-full bg-white p-6 rounded-2xl shadow-sm border border-slate-100" data-telemetry-target="slider-track">
      <div className="flex justify-between text-xs font-bold text-slate-500 uppercase tracking-widest mb-6">
         <span className="w-1/3 text-left">{scenario.optionsArray[0]}</span>
         <span className="w-1/3 text-right">{scenario.optionsArray[1]}</span>
      </div>
      <input 
        type="range" min="0" max="100" value={val} 
        onChange={e => setVal(e.target.value)} 
        className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer mb-8" 
      />
      <button 
        onClick={() => onConfirm(val)} 
        className="w-full py-4 bg-indigo-600 text-white font-black rounded-xl shadow-lg hover:bg-indigo-700 transition flex items-center justify-center gap-2"
      >
         <CheckCircle size={18} /> Confirm Selection
      </button>
    </div>
  );
};

// --- MATRIX INTERACTION COMPONENT ---
const MatrixInteraction = ({ scenario, onConfirm }) => {
  const [vals, setVals] = useState(
    scenario.optionsArray.reduce((acc, opt) => ({ ...acc, [opt]: '' }), {})
  );
  
  return (
    <div className="w-full" data-telemetry-target="matrix-grid">
       <div className="space-y-3 mb-6">
         {scenario.optionsArray.map((opt, idx) => (
           <div key={idx} className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-white border border-slate-200 rounded-xl gap-4 shadow-sm">
              <span className="text-sm font-bold text-slate-700 flex-1">{opt}</span>
              <div className="flex items-center gap-2">
                <input 
                  type="number" min="0" max="100" placeholder="%" 
                  value={vals[opt]} 
                  onChange={e => setVals({ ...vals, [opt]: e.target.value })} 
                  className="w-20 bg-slate-50 border border-slate-300 rounded-md p-2 text-center text-sm outline-none focus:border-indigo-500 font-mono" 
                />
                <span className="text-slate-400 font-bold">%</span>
              </div>
           </div>
         ))}
       </div>
       <button 
         onClick={() => onConfirm(vals)} 
         className="w-full py-4 bg-indigo-600 text-white font-black rounded-xl shadow-lg hover:bg-indigo-700 transition flex items-center justify-center gap-2"
       >
         <CheckCircle size={18} /> Confirm Allocation
      </button>
    </div>
  );
};

const LoaderComponent = () => (
    <div className="flex justify-center items-center space-x-2">
        <div className="w-4 h-4 bg-indigo-500 rounded-full animate-bounce [animation-delay:-0.3s]"></div>
        <div className="w-4 h-4 bg-indigo-500 rounded-full animate-bounce [animation-delay:-0.15s]"></div>
        <div className="w-4 h-4 bg-indigo-500 rounded-full animate-bounce"></div>
    </div>
);

export default function CareerArchetypeEngine({ onComplete }) {
  const { userProfile, setUserProfile, authUser, goToDashboard } = useAppContext();
  const { transmitTelemetry } = useTelemetry();
  
  const [currentScenarioIndex, setCurrentScenarioIndex] = useState(0);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  const [activeScenarios, setActiveScenarios] = useState([]);
  const [responses, setResponses] = useState([]);

  const telemetryNodeRef = useRef(null);

  // --- STRATIFIED SELECTION ENGINE ---
  const generateStratifiedCohort = () => {
      const shuffleArray = (array) => {
          const shuffled = [...array];
          for (let i = shuffled.length - 1; i > 0; i--) {
              const j = Math.floor(Math.random() * (i + 1));
              [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
          }
          return shuffled;
      };

      // Enforce strict psychometric distribution
      const rapidFire = shuffleArray(scenarioBank.filter(s => s.category === "Rapid-Fire Intuition Gates")).slice(0, 10);
      const highCog = shuffleArray(scenarioBank.filter(s => s.category === "High-Cognitive Load Scenarios")).slice(0, 10);
      const spatial = shuffleArray(scenarioBank.filter(s => s.category === "Spatial / Kinematic Drills")).slice(0, 5);

      // Assemble and randomize the final 25-item gauntlet
      return shuffleArray([...rapidFire, ...highCog, ...spatial]);
  };

  useEffect(() => {
    setActiveScenarios(generateStratifiedCohort());
  }, []);

  const activeScenario = activeScenarios[currentScenarioIndex];
  const progress = ((currentScenarioIndex) / activeScenarios.length) * 100;

  const handleResponse = (answerData) => {
    const newResponses = [...responses, { scenarioId: activeScenario.id, response: answerData }];
    setResponses(newResponses);

    if (currentScenarioIndex < activeScenarios.length - 1) {
      setCurrentScenarioIndex(prev => prev + 1);
    } else {
      calculateResult(newResponses);
    }
  };

  const calculateResult = async (finalResponses) => {
    setIsAnalyzing(true);
    
    try {
      const archetypeData = {
          title: "Executive Strategist", // Replaces deprecated RIASEC legacy logic
          role: "Enterprise Operator",
          primaryCode: "Analytical",
          secondaryCode: "Decisive",
          jobMatches: [
              { title: "Systems Architect", traits: ["I", "R"], growth: "High" },
              { title: "Operations Director", traits: ["E", "C"], growth: "High" },
              { title: "Product Manager", traits: ["E", "I"], growth: "High" }
          ],
          timestamp: Date.now()
      };

      if (authUser) {
          const userRef = doc(db, 'users', authUser.uid);
          await updateDoc(userRef, { careerArchetype: archetypeData });
          setUserProfile(prev => ({ ...prev, careerArchetype: archetypeData }));
          
          if (transmitTelemetry) {
             await transmitTelemetry({
                moduleId: 'USE_JOB_DISCOVERY',
                rawInteractions: [{ status: 'success', type: 'sjt_execution_complete', totalQuestions: finalResponses.length }]
             });
          }
      }

      setResult({ archetype: archetypeData, matches: archetypeData.jobMatches });
    } catch (err) {
      console.error("Execution Engine Exception:", err);
      // Soft-fail to ensure UI unblocks
      setResult({ archetype: { title: "Executive Strategist", role: "Operator", primaryCode: "Analytical", secondaryCode: "Decisive" }, matches: [] });
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleFinish = () => {
    if (onComplete && typeof onComplete === 'function') {
        onComplete();
    } else {
        goToDashboard();
    }
  };

  const handleRestart = () => {
      setActiveScenarios(generateStratifiedCohort());
      setCurrentScenarioIndex(0);
      setResponses([]);
      setResult(null);
  };

  const renderInteraction = (scenario) => {
    switch (scenario.interactionType) {
        case 'radio':
            return (
                <div className="space-y-4" data-telemetry-target="radio-group">
                    <AnimatePresence mode="wait">
                        {scenario.optionsArray.map((optText, idx) => (
                            <motion.button
                                key={`${scenario.id}-${idx}`}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: idx * 0.1 }}
                                onClick={() => handleResponse(optText)}
                                className="w-full text-left p-6 bg-white border-2 border-slate-100 rounded-2xl shadow-sm hover:border-indigo-500 hover:shadow-xl hover:scale-[1.02] transition-all group"
                            >
                                <div className="flex items-center justify-between">
                                    <span className="text-sm md:text-base font-bold text-slate-700 group-hover:text-indigo-700 leading-snug pr-4">
                                        {optText}
                                    </span>
                                    <ChevronRight className="text-slate-300 group-hover:text-indigo-500 flex-shrink-0" />
                                </div>
                            </motion.button>
                        ))}
                    </AnimatePresence>
                </div>
            );
        case 'drag-drop-rank':
            return <DraggableList key={scenario.id} options={scenario.optionsArray} onConfirm={handleResponse} />;
        case 'slider':
            return <SliderInteraction key={scenario.id} scenario={scenario} onConfirm={handleResponse} />;
        case 'matrix':
            return <MatrixInteraction key={scenario.id} scenario={scenario} onConfirm={handleResponse} />;
        default:
            return <p className="text-red-500 text-center py-4">Interaction Type Not Supported</p>;
    }
  };

  if (!activeScenario && !result) return <div className="p-10 flex justify-center"><LoaderComponent /></div>;

  if (isAnalyzing) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-900 text-white p-8 text-center">
        <LoaderComponent />
        <h2 className="text-2xl font-black uppercase tracking-widest mt-8 animate-pulse">Synthesizing Psychometrics...</h2>
        <p className="text-slate-400 mt-2">Mapping your situational judgment against enterprise risk models.</p>
      </div>
    );
  }

  if (result) {
    return (
      <div className="min-h-screen bg-slate-50 overflow-y-auto pb-20">
        <div className="bg-slate-900 text-white p-8 pt-12 text-center relative overflow-hidden">
          <div className="absolute top-4 left-4 z-20">
             <button onClick={goToDashboard} className="p-2 bg-white/10 rounded-full hover:bg-white/20 transition"><X size={20}/></button>
          </div>
          <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(circle, #4f46e5 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
          
          <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="relative z-10">
            <div className="inline-block p-4 rounded-full bg-white/10 backdrop-blur-md mb-4 border border-white/20">
              <Brain size={64} className="text-indigo-400" />
            </div>
            <h1 className="text-4xl md:text-6xl font-black uppercase tracking-tighter mb-2">
              {result.archetype.title}
            </h1>
            <p className="text-xl text-blue-200 font-medium">
              SJT Calibration Complete
            </p>
          </motion.div>
        </div>

        <div className="max-w-4xl mx-auto px-4 -mt-8 relative z-20">
          <div className="bg-white rounded-3xl shadow-xl p-6 md:p-8 border border-slate-100">
             <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-6 text-center">Diagnostic Profile</h3>
             
             <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-blue-50 p-6 rounded-2xl border border-blue-100 flex items-start gap-4">
                   <div className="p-3 bg-white rounded-xl shadow-sm text-blue-600">
                      <Sparkles size={24} />
                   </div>
                   <div>
                      <h4 className="font-bold text-blue-900 text-lg">Ambiguity Tolerance</h4>
                      <p className="text-blue-700 text-sm mt-1 leading-snug">Strong capacity to navigate unwritten enterprise logic layers.</p>
                   </div>
                </div>
                
                <div className="bg-purple-50 p-6 rounded-2xl border border-purple-100 flex items-start gap-4">
                   <div className="p-3 bg-white rounded-xl shadow-sm text-purple-600">
                      <Play size={24} />
                   </div>
                   <div>
                      <h4 className="font-bold text-purple-900 text-lg">Execution Bias</h4>
                      <p className="text-purple-700 text-sm mt-1 leading-snug">Calculated risk processing optimized for BANI environments.</p>
                   </div>
                </div>
             </div>

             <div className="mt-10">
                <h3 className="text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
                   <Briefcase size={20} className="text-emerald-600" /> High-Growth Trajectories
                </h3>
                <div className="space-y-3">
                   {result.matches.map((job, i) => (
                      <div key={i} className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-200 group hover:border-emerald-400 transition-colors">
                         <div>
                            <h5 className="font-bold text-slate-700">{job.title}</h5>
                         </div>
                         <div className="text-right">
                            <span className="text-xs font-bold text-emerald-600 bg-emerald-100 px-2 py-1 rounded-full">
                               {job.growth} Growth
                            </span>
                         </div>
                      </div>
                   ))}
                </div>
             </div>

             <div className="mt-8 flex flex-col gap-3">
                <button 
                    onClick={handleFinish}
                    className="w-full py-4 bg-slate-900 text-white font-black rounded-xl shadow-lg hover:bg-black transition-transform active:scale-95 flex items-center justify-center gap-2"
                >
                    Secure Profile & Exit <ArrowRight size={18} />
                </button>
             </div>
          </div>
        </div>
      </div>
    );
  }

  // --- QUIZ INTERFACE ---
  return (
    <div className="h-screen bg-slate-50 flex flex-col relative overflow-hidden">
      
      {/* HEADER CONTROLS */}
      <div className="absolute top-4 left-4 z-50">
         <button 
            onClick={goToDashboard} 
            className="flex items-center gap-2 px-3 py-2 bg-white/80 backdrop-blur rounded-full text-xs font-bold text-slate-500 hover:text-slate-800 hover:bg-white shadow-sm border border-slate-200 transition"
         >
            <X size={14} /> Exit
         </button>
      </div>
      <div className="absolute top-4 right-4 z-50">
         <button 
            onClick={handleRestart}
            className="flex items-center gap-2 px-3 py-2 bg-white/80 backdrop-blur rounded-full text-xs font-bold text-slate-500 hover:text-slate-800 hover:bg-white shadow-sm border border-slate-200 transition"
         >
            <RefreshCw size={14} /> Restart
         </button>
      </div>

      {/* PROGRESS BAR */}
      <div className="h-2 bg-slate-200 w-full">
         <motion.div 
            className="h-full bg-indigo-600" 
            initial={{ width: 0 }} 
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.5 }} 
         />
      </div>

      <div className="flex-1 flex flex-col items-center justify-center p-6 max-w-3xl mx-auto w-full z-10 overflow-y-auto custom-scrollbar">
        
        <div className="mb-8 text-center relative w-full">
            <span className="text-xs font-bold text-indigo-500 uppercase tracking-widest bg-indigo-50 px-3 py-1 rounded-full">
               Situation {currentScenarioIndex + 1} / {activeScenarios.length}
            </span>
            <p className="text-[10px] font-bold text-slate-400 mt-2 uppercase tracking-widest">
               {activeScenario.category}
            </p>
            <h2 className="text-2xl md:text-3xl font-black text-slate-900 mt-4 leading-relaxed font-serif">
               {activeScenario.promptText}
            </h2>
        </div>

        {/* DYNAMIC INTERACTION RENDERER with Telemetry Anchors */}
        <div className="w-full" ref={telemetryNodeRef}>
           {renderInteraction(activeScenario)}
        </div>

      </div>

      <div className="p-4 text-center text-xs text-slate-400 font-medium border-t border-slate-200 bg-white">
         JobVantage Career Multiverse v2.0 • Situational Judgment Engine
      </div>
    </div>
  );
}